import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Alert as MuiAlert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import therapistApi from '../../services/therapistApi';

/**
 * AlertListCard Component
 *
 * Displays a list of conversation alerts for a specific client
 * with actions to acknowledge, resolve, or dismiss alerts.
 *
 * Props:
 * - clientId: ID of the client
 * - token: Authentication token
 * - showPendingOnly: Boolean to filter only pending alerts (default: false)
 */
function AlertListCard({ clientId, token, showPendingOnly = false }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAlerts, setExpandedAlerts] = useState(new Set());
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (clientId && token) {
      fetchAlerts();
    }
  }, [clientId, token, showPendingOnly]);

  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await therapistApi.getClientAlerts(clientId, token);
      let fetchedAlerts = response.alerts || [];

      // Filter by pending if requested
      if (showPendingOnly) {
        fetchedAlerts = fetchedAlerts.filter(alert => alert.status === 'pending');
      }

      setAlerts(fetchedAlerts);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (alertId) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const handleAcknowledge = async (alertId) => {
    setActionLoading(true);
    try {
      await therapistApi.acknowledgeAlert(alertId, token);
      await fetchAlerts(); // Refresh list
    } catch (err) {
      console.error('Error acknowledging alert:', err);
      setError('Failed to acknowledge alert');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveClick = (alert) => {
    setSelectedAlert(alert);
    setResolutionNotes('');
    setResolveDialogOpen(true);
  };

  const handleResolveConfirm = async () => {
    if (!selectedAlert || !resolutionNotes.trim()) return;

    setActionLoading(true);
    try {
      await therapistApi.resolveAlert(selectedAlert._id, resolutionNotes, token);
      setResolveDialogOpen(false);
      setSelectedAlert(null);
      setResolutionNotes('');
      await fetchAlerts(); // Refresh list
    } catch (err) {
      console.error('Error resolving alert:', err);
      setError('Failed to resolve alert');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = async (alertId, reason = 'Reviewed by therapist') => {
    setActionLoading(true);
    try {
      await therapistApi.dismissAlert(alertId, reason, token);
      await fetchAlerts(); // Refresh list
    } catch (err) {
      console.error('Error dismissing alert:', err);
      setError('Failed to dismiss alert');
    } finally {
      setActionLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'error';  // Highest priority - life-threatening
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCrisisTypeLabel = (crisisType) => {
    if (!crisisType) return null;
    const labels = {
      'self_harm': '🚨 Self-Harm',
      'suicide_ideation': '🚨 Suicide Ideation',
      'psychosis': '⚠️ Psychosis',
      'severe_trauma': 'Severe Trauma',
      'substance_abuse_crisis': 'Substance Abuse Crisis',
      'eating_disorder_crisis': 'Eating Disorder Crisis',
      'multi_domain_complexity': 'Multi-Domain Complexity',
      'contraindication': 'Contraindication',
      'safety_concern': 'Safety Concern'
    };
    return labels[crisisType] || crisisType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getSourceLabel = (source) => {
    if (!source) return null;
    const labels = {
      'llm_conversation': 'AI Detected',
      'triage_assessment': 'Triage Assessment',
      'psychosis_risk': 'Psychosis Screening',
      'mode_change_crisis': 'Crisis Mode Change',
      'assessment_crisis_score': 'Assessment Score',
      'dropped_safety_screen': 'Dropped Safety Screen',
      'manual_therapist': 'Manual (Therapist)',
      'manual_supervisor': 'Manual (Supervisor)',
      'backfill': 'Historical'
    };
    return labels[source] || source;
  };

  const getSLAStatus = (alert) => {
    if (!alert.sla || !alert.sla.responseDeadline) return null;

    const now = new Date();
    const deadline = new Date(alert.sla.responseDeadline);
    const timeRemaining = deadline - now;

    if (alert.status === 'resolved' || alert.status === 'dismissed') {
      return null; // Don't show SLA for completed alerts
    }

    if (timeRemaining < 0) {
      return {
        status: 'breached',
        label: 'SLA BREACHED',
        color: 'error',
        icon: <ErrorIcon />
      };
    } else if (timeRemaining < 15 * 60 * 1000) { // Less than 15 minutes
      return {
        status: 'critical',
        label: `${Math.floor(timeRemaining / 60000)} min remaining`,
        color: 'error',
        icon: <WarningIcon />
      };
    } else if (timeRemaining < 60 * 60 * 1000) { // Less than 1 hour
      return {
        status: 'warning',
        label: `${Math.floor(timeRemaining / 60000)} min remaining`,
        color: 'warning',
        icon: <WarningIcon />
      };
    }

    return null; // On track
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'acknowledged':
        return 'info';
      case 'resolved':
        return 'success';
      case 'dismissed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'CARE_NOTE_CONCERN':
        return 'Care Note Concern';
      case 'CRISIS_ALERT':
        return 'Crisis Alert';
      case 'SAFETY_ISSUE':
        return 'Safety Issue';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <MuiAlert severity="error">{error}</MuiAlert>
        </CardContent>
      </Card>
    );
  }

  const pendingCount = alerts.filter(a => a.status === 'pending').length;

  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Client Alerts
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {pendingCount > 0 && (
                <Chip
                  label={`${pendingCount} Pending`}
                  color="warning"
                  size="small"
                />
              )}
              <Typography variant="body2" color="text.secondary">
                {alerts.length} Total
              </Typography>
            </Box>
          </Box>

          {alerts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No alerts for this client
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {alerts.map((alert, index) => (
                <React.Fragment key={alert._id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      py: 2,
                      backgroundColor: alert.status === 'pending' && alert.priority === 'urgent'
                        ? 'rgba(211, 47, 47, 0.05)'
                        : 'transparent'
                    }}
                  >
                    {/* Alert Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          icon={alert.priority === 'critical' || alert.priority === 'urgent' || alert.priority === 'high' ? <ErrorIcon /> : <WarningIcon />}
                          label={getAlertTypeLabel(alert.type)}
                          color={getPriorityColor(alert.priority)}
                          size="small"
                        />

                        {/* Priority Chip - Enhanced for CRITICAL */}
                        <Chip
                          label={alert.priority === 'critical' ? '⚠️ CRITICAL' : `Priority: ${alert.priority?.toUpperCase()}`}
                          color={getPriorityColor(alert.priority)}
                          size="small"
                          variant={alert.priority === 'critical' ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: alert.priority === 'critical' ? 'bold' : 'normal',
                            animation: alert.priority === 'critical' ? 'pulse 2s ease-in-out infinite' : 'none',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.7 }
                            }
                          }}
                        />

                        {/* Crisis Type Badge - NEW */}
                        {alert.metadata?.crisisType && (
                          <Chip
                            label={getCrisisTypeLabel(alert.metadata.crisisType)}
                            color="error"
                            size="small"
                            variant="outlined"
                          />
                        )}

                        {/* Status Chip */}
                        <Chip
                          label={alert.status}
                          color={getStatusColor(alert.status)}
                          size="small"
                        />

                        {/* Source Badge - NEW */}
                        {alert.metadata?.source && (
                          <Chip
                            label={getSourceLabel(alert.metadata.source)}
                            size="small"
                            variant="outlined"
                            sx={{ borderStyle: 'dashed' }}
                          />
                        )}

                        {/* SLA Status Badge - NEW */}
                        {(() => {
                          const slaStatus = getSLAStatus(alert);
                          return slaStatus ? (
                            <Chip
                              icon={slaStatus.icon}
                              label={slaStatus.label}
                              color={slaStatus.color}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          ) : null;
                        })()}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                      </Typography>
                    </Box>

                    {/* Alert Concern Summary */}
                    {alert.concern && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          Concern: {alert.concern.concernType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {alert.concern.explanation}
                        </Typography>
                        {alert.concern.suggestedAction && (
                          <Chip
                            label={`Action: ${alert.concern.suggestedAction.replace(/_/g, ' ')}`}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                    )}

                    {/* Expand/Collapse Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Button
                        size="small"
                        onClick={() => toggleExpand(alert._id)}
                        endIcon={expandedAlerts.has(alert._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        {expandedAlerts.has(alert._id) ? 'Show Less' : 'Show More'}
                      </Button>
                    </Box>

                    {/* Expanded Details */}
                    <Collapse in={expandedAlerts.has(alert._id)}>
                      <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: 'divider' }}>
                        {/* SLA Information - NEW */}
                        {alert.sla && (
                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                              📊 SLA Tracking
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                              <Typography variant="caption" display="block">
                                <strong>Target Response:</strong> {alert.sla.targetResponseTime} minutes
                              </Typography>
                              {alert.sla.responseDeadline && (
                                <Typography variant="caption" display="block">
                                  <strong>Deadline:</strong> {format(new Date(alert.sla.responseDeadline), 'PPpp')}
                                </Typography>
                              )}
                              {alert.sla.timeToAcknowledge && (
                                <Typography variant="caption" display="block" color="success.main">
                                  <strong>Acknowledged in:</strong> {alert.sla.timeToAcknowledge} min
                                </Typography>
                              )}
                              {alert.sla.timeToResolve && (
                                <Typography variant="caption" display="block" color="success.main">
                                  <strong>Resolved in:</strong> {alert.sla.timeToResolve} min
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Timeline - NEW */}
                        {alert.timeline && alert.timeline.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                              📝 Timeline
                            </Typography>
                            <List dense disablePadding>
                              {alert.timeline.map((entry, idx) => (
                                <ListItem key={idx} sx={{ py: 0.5 }}>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                          label={entry.status}
                                          size="small"
                                          color={getStatusColor(entry.status)}
                                          sx={{ minWidth: 100 }}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                          {format(new Date(entry.timestamp), 'PPpp')}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                        {entry.note || 'No notes'}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {/* Metadata - NEW */}
                        {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                              ℹ️ Detection Details
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {alert.metadata.source && (
                                <Typography variant="caption">
                                  <strong>Source:</strong> {getSourceLabel(alert.metadata.source)}
                                </Typography>
                              )}
                              {alert.metadata.crisisType && (
                                <Typography variant="caption">
                                  <strong>Crisis Type:</strong> {getCrisisTypeLabel(alert.metadata.crisisType)}
                                </Typography>
                              )}
                              {alert.metadata.strategyKey && (
                                <Typography variant="caption">
                                  <strong>Triage Strategy:</strong> {alert.metadata.strategyKey}
                                </Typography>
                              )}
                              {alert.metadata.matchedRule && (
                                <Typography variant="caption">
                                  <strong>Matched Rule:</strong> {alert.metadata.matchedRule}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Legacy Timestamps (backward compatible) */}
                        <Typography variant="caption" display="block" gutterBottom>
                          <strong>Created:</strong> {format(new Date(alert.createdAt), 'PPpp')}
                        </Typography>
                        {alert.acknowledgedAt && !alert.timeline && (
                          <Typography variant="caption" display="block" gutterBottom>
                            <strong>Acknowledged:</strong> {format(new Date(alert.acknowledgedAt), 'PPpp')}
                          </Typography>
                        )}
                        {alert.resolvedAt && !alert.timeline && (
                          <Typography variant="caption" display="block" gutterBottom>
                            <strong>Resolved:</strong> {format(new Date(alert.resolvedAt), 'PPpp')}
                          </Typography>
                        )}
                        {alert.resolution && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Resolution Notes:</strong> {alert.resolution}
                          </Typography>
                        )}

                        {/* Action Buttons */}
                        {alert.status === 'pending' && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleAcknowledge(alert._id)}
                              disabled={actionLoading}
                            >
                              Acknowledge
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleResolveClick(alert)}
                              disabled={actionLoading}
                            >
                              Resolve
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleDismiss(alert._id)}
                              disabled={actionLoading}
                            >
                              Dismiss
                            </Button>
                          </Box>
                        )}
                        {alert.status === 'acknowledged' && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleResolveClick(alert)}
                              disabled={actionLoading}
                            >
                              Resolve
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleDismiss(alert._id)}
                              disabled={actionLoading}
                            >
                              Dismiss
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Alert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide resolution notes for this alert. This will mark the alert as resolved.
          </Typography>
          <TextField
            label="Resolution Notes"
            multiline
            rows={4}
            fullWidth
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            placeholder="Describe how this concern was addressed..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleResolveConfirm}
            variant="contained"
            color="success"
            disabled={actionLoading || !resolutionNotes.trim()}
          >
            Resolve Alert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AlertListCard;
